import { Close, LocationOn, Public, Search } from "@mui/icons-material";
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    Fade,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Zoom
} from "@mui/material";
import { useEffect, useState } from "react";
import { Grid } from "react-loader-spinner";
import { useDispatch } from "react-redux";
import Utility from "../api/utility";
import { show } from '../reducers/notificationSlice';

export default function State({ code, setCountryCode }) {
    const [states, setStates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();

    function showStates(code) {
        setLoading(true);
        Utility.getStates(code)
            .then(({ data }) => {
                setStates(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((err) => {
                dispatch(show({ message: err.response.data.message, open: true, severity: "error" }));
                setLoading(false);
            });
    }

    useEffect(() => {
        if (code) {
            showStates(code);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    function handleClose() {
        setCountryCode("");
        setSearchTerm('');
    }

    const filteredStates = states.filter((state) =>
        state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Dialog
            open={Boolean(code)}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    maxHeight: '90vh'
                }
            }}
        >
            {/* Header with gradient */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 3
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Public sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Provinces / States
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Total: {filteredStates.length} {filteredStates.length !== states.length && `of ${states.length}`}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>

                {/* Search */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search states..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '12px',
                            color: 'white',
                            '& fieldset': {
                                borderColor: 'rgba(255,255,255,0.3)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(255,255,255,0.5)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgba(255,255,255,0.7)',
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: 'rgba(255,255,255,0.7)',
                            opacity: 1
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'rgba(255,255,255,0.7)' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                        <Grid color="#667eea" />
                    </Box>
                ) : (
                    <>
                        {filteredStates.length > 0 ? (
                            <Box sx={{ p: 3 }}>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, 1fr)',
                                        md: 'repeat(3, 1fr)'
                                    },
                                    gap: 2
                                }}>
                                    {filteredStates.map((state, index) => (
                                        <Zoom
                                            in={true}
                                            key={state.id}
                                            style={{ transitionDelay: `${index * 30}ms` }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 2.5,
                                                    borderRadius: '16px',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    backgroundColor: 'background.paper',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                                                        transform: 'translateY(-4px)',
                                                        '& .location-icon': {
                                                            transform: 'scale(1.1) rotate(5deg)',
                                                            color: 'primary.main'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                    <Box sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '10px',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <LocationOn
                                                            className="location-icon"
                                                            sx={{
                                                                color: 'white',
                                                                fontSize: 22,
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '1rem',
                                                                mb: 0.5,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {state.name}
                                                        </Typography>
                                                        <Chip
                                                            label={state.code}
                                                            size="small"
                                                            sx={{
                                                                height: 22,
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                bgcolor: 'action.hover',
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Zoom>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <Fade in={true}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '300px',
                                    p: 4,
                                    gap: 2
                                }}>
                                    <Box sx={{
                                        width: 100,
                                        height: 100,
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Search sx={{ fontSize: 50, color: 'text.disabled' }} />
                                    </Box>
                                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {searchTerm ? 'No states match your search' : 'No states found'}
                                    </Typography>
                                    {searchTerm && (
                                        <Typography variant="body2" color="text.secondary">
                                            Try a different search term
                                        </Typography>
                                    )}
                                </Box>
                            </Fade>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
